import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { fetchStores } from '../api/queries';
import { safeStorage } from '../utils/storage';

const StoreContext = createContext();

export function StoreProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [selectedStoreId, setSelectedStoreId] = useState(() => {
    const storedId = safeStorage.get('selectedStoreId');
    return storedId == null ? null : String(storedId);
  });
  const [isTokenReady, setIsTokenReady] = useState(false);

  useEffect(() => {
    if (user) {
      user.getIdToken(true)
        .then(() => setIsTokenReady(true))
        .catch(console.error);
    }
  }, [user]);

  const { data: allStores, isLoading } = useQuery({
    queryKey: ['stores'],
    queryFn: fetchStores,
    enabled: isAuthenticated && isTokenReady,
  });

  // GET /v1/stores also returns stores where this user is a scanner-only member
  // (the scanner app needs them). The dashboard must not offer those: the API
  // refuses every dashboard call for them, so they are dropped here, before
  // selection, and the switcher never sees them.
  const stores = useMemo(
    () => allStores?.filter((store) => store.current_user_dashboard_access !== false),
    [allStores]
  );
  // Signed in fine, but every store is scanner-only: StoreCheckWrapper shows
  // the "use the Scanner app" screen instead of sending them to create a store.
  const isScannerOnly = allStores?.length > 0 && stores.length === 0;

  useEffect(() => {
    if (stores?.length > 0 && (!selectedStoreId || !stores.find(store => String(store.id) === String(selectedStoreId)))) {
      const newStoreId = String(stores[0].id);
      safeStorage.set('selectedStoreId', newStoreId);
      setSelectedStoreId(newStoreId);
    }
  }, [stores, selectedStoreId]);

  const selectedStore = stores?.find(store => String(store.id) === String(selectedStoreId));

  // Owners and admins configure the store; members only operate it. The API
  // enforces this, so screens use it to not offer what would be refused. An
  // older API without current_user_role keeps everything visible, as before.
  const canManageStore = selectedStore?.current_user_role !== 'member';

  return (
    <StoreContext.Provider value={{ 
      selectedStoreId,
      selectedStore,
      updateSelectedStore: (id) => {
        const newId = String(id);
        safeStorage.set('selectedStoreId', newId);
        setSelectedStoreId(newId);
      }, 
      stores,
      isScannerOnly,
      canManageStore,
      isLoading: isLoading || !isTokenReady
    }}>
      {children}
    </StoreContext.Provider>
  );
}

export function useStore() {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
} 